import type { DefaultPrivacyLevel } from '@datadog/browser-rum';

/**
 * Session Replay configuration, sourced from the `SESSION_REPLAY_CONFIG` feature
 * flag. Everything here is provider agnostic: it parses a raw JSON config string
 * and knows nothing about Split, Harness or (soon) OpenFeature. Only the hook that
 * reads the flag is provider specific, so swapping providers touches one file.
 *
 * The config is intentionally flat, booleans and integers only, to keep it
 * foolproof to edit in the flag dashboard:
 *
 *   { "unmask": false, "viewerSampleRate": 0, "worklistSampleRate": 0, "reporterSampleRate": 0 }
 *
 * Masking is a single per session value because `defaultPrivacyLevel` is fixed at
 * `datadogRum.init` time and applies to the whole session. Sampling is per surface
 * because we control recording ourselves via start/stop.
 */

export type ReplaySurface = 'viewer' | 'worklist' | 'reporter';

export interface SessionReplayConfig {
  unmask: boolean;
  viewerSampleRate: number;
  worklistSampleRate: number;
  reporterSampleRate: number;
}

export const DEFAULT_SESSION_REPLAY_CONFIG: SessionReplayConfig = {
  unmask: false,
  viewerSampleRate: 0,
  worklistSampleRate: 0,
  reporterSampleRate: 0,
};

function clampRate(value: unknown): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}

/**
 * Parse the raw flag config string into a {SessionReplayConfig}. Any malformed,
 * missing or unexpected value falls back to the safe default (off and masked), so
 * a bad config can never accidentally enable recording or unmask a session.
 */
export function parseSessionReplayConfig(raw: Nullable<string>): SessionReplayConfig {
  if (raw == null) return DEFAULT_SESSION_REPLAY_CONFIG;

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      unmask: parsed.unmask === true,
      viewerSampleRate: clampRate(parsed.viewerSampleRate),
      worklistSampleRate: clampRate(parsed.worklistSampleRate),
      reporterSampleRate: clampRate(parsed.reporterSampleRate),
    };
  } catch {
    return DEFAULT_SESSION_REPLAY_CONFIG;
  }
}

export function privacyLevelFor(config: SessionReplayConfig): DefaultPrivacyLevel {
  return config.unmask ? 'allow' : 'mask';
}

export function sampleRateForSurface(config: SessionReplayConfig, surface: ReplaySurface): number {
  switch (surface) {
    case 'viewer':
      return config.viewerSampleRate;
    case 'worklist':
      return config.worklistSampleRate;
    case 'reporter':
      return config.reporterSampleRate;
  }
}

/**
 * Decide once, for the lifetime of this page session, whether to record a surface.
 * `roll` is injectable so tests are deterministic.
 */
export function isSurfaceSampled(sampleRate: number, roll: number = Math.random() * 100): boolean {
  return sampleRate > 0 && roll < sampleRate;
}

export function surfaceForPath(pathname: string): Nullable<ReplaySurface> {
  if (pathname.includes('/viewer')) return 'viewer';
  if (pathname.includes('/reporter')) return 'reporter';
  if (pathname.includes('/worklist')) return 'worklist';
  return null;
}
